import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import marketDataService from '../services/marketDataService';
import { 
  ChartBarIcon, 
  ShieldCheckIcon, 
  BoltIcon,
  ArrowRightIcon,
  LockClosedIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const Home = () => {
  const [activeMarketTab, setActiveMarketTab] = useState('crypto');
  const [liveMarketData, setLiveMarketData] = useState({ crypto: [], stocks: [], indices: [] });

  const fetchMarketData = async () => {
    try {
      const [cryptoData, stockPrices, indices] = await Promise.all([
        marketDataService.getCryptoPrices(),
        marketDataService.getStockPrices(),
        marketDataService.getMarketIndices()
      ]);
      setLiveMarketData({ crypto: cryptoData || [], stocks: stockPrices || [], indices: indices || [] });
    } catch (error) {
      setLiveMarketData({ crypto: [], stocks: [], indices: [] });
    }
  };

  useEffect(() => {
    fetchMarketData();
    const interval = setInterval(fetchMarketData, 30000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      name: 'James Okafor',
      role: 'Senior Portfolio Manager',
      company: 'Meridian Wealth Partners',
      content: 'INTRA CAPITAL has completely changed the way I manage client portfolios. The platform\'s execution speed and depth of market data give us a genuine edge that our clients feel every quarter.',
      rating: 5,
      image: '/images/people/pexels-tima-miroshnichenko-5439443.jpg',
      stats: '+218% Portfolio Growth'
    },
    {
      name: 'Amara Nwosu',
      role: 'Head of Digital Assets',
      company: 'Apex Capital Group',
      content: 'We moved our entire crypto desk to INTRA CAPITAL and never looked back. The liquidity, the security architecture, and the compliance tools are genuinely institutional-grade.',
      rating: 5,
      image: '/images/people/pexels-mart-production-7709127.jpg',
      stats: '$120M+ Assets Managed'
    },
    {
      name: 'Daniel Ferreira',
      role: 'Quantitative Strategist',
      company: 'Vortex Trading Systems',
      content: 'I\'ve tested dozens of platforms for systematic strategies. INTRA CAPITAL\'s API reliability and sub-millisecond execution are in a class of their own. It\'s the backbone of our entire operation.',
      rating: 5,
      image: '/images/people/pexels-tima-miroshnichenko-5336962.jpg',
      stats: '2M+ Automated Trades'
    },
    {
      name: 'Priya Menon',
      role: 'Independent Investor',
      company: 'Self-Managed Fund',
      content: 'As a self-directed investor, I needed a platform that matched my ambition. INTRA CAPITAL gives me the same tools the big funds use — with a clean interface I can actually navigate.',
      rating: 5,
      image: '/images/people/pexels-mart-production-7222273.jpg',
      stats: '+340% in 18 Months'
    }
  ];

  const services = [
    {
      image: '/images/new-images/copy-trading.jpg',
      tag: 'Copy Trading',
      title: 'Mirror the Best.\nKeep the Gains.',
      description: 'Automatically replicate trades from top-performing investors in real time. No experience required — just pick a trader, set your allocation, and let their expertise work for you.',
      bullets: ['1-click copy from verified traders', 'Adjustable risk & position sizing', 'Full performance transparency'],
      href: '/copy-trading',
      accent: 'amber',
      imageRight: false,
    },
    {
      image: '/images/new-images/bots-trading.jpg',
      tag: 'Bots Trading',
      title: 'Automate.\nOptimise. Profit.',
      description: 'Deploy AI-powered trading bots that work around the clock. Build custom strategies or choose from pre-built algorithms — all executing with institutional-grade precision 24/7.',
      bullets: ['24/7 automated execution', 'Strategy builder & backtesting', 'Multi-asset bot deployment'],
      href: '/bots-trading',
      accent: 'violet',
      imageRight: true,
    },
    {
      image: '/images/new-images/live-trading.jpg',
      tag: 'Live Trading',
      title: 'Real Markets.\nReal Time.',
      description: 'Trade live across global markets with sub-millisecond execution, advanced charting, and professional-grade order types. Everything you need to act with confidence.',
      bullets: ['Sub-ms order execution', '100+ technical indicators', 'Direct market access'],
      href: '/live-trading',
      accent: 'emerald',
      imageRight: false,
    },
    {
      image: '/images/new-images/stock-trading.jpg',
      tag: 'Stocks & Equities',
      title: 'Global Equities\nat Your Fingertips.',
      description: 'Access thousands of stocks across major global exchanges. Backed by real-time quotes, deep fundamental research, and powerful portfolio management tools.',
      bullets: ['NYSE, NASDAQ, LSE & more', 'Real-time quotes & screener', 'Dividend & earnings tracker'],
      href: '/markets?type=stocks',
      accent: 'sky',
      imageRight: true,
    },
    {
      image: '/images/new-images/crypto-trading.jpg',
      tag: 'Crypto & Digital Assets',
      title: 'The Future of\nFinance is Here.',
      description: 'Trade 200+ cryptocurrencies with deep liquidity, tight spreads, and zero compromise on security. From Bitcoin to emerging DeFi tokens — all in one place.',
      bullets: ['200+ crypto assets', 'DeFi & yield strategies', 'Cold-storage grade security'],
      href: '/markets?type=crypto',
      accent: 'amber',
      imageRight: false,
    },
    {
      image: '/images/new-images/p2p-trading.jpg',
      tag: 'P2P Exchange',
      title: 'Trade Directly.\nNo Middleman.',
      description: 'Connect and trade with verified counterparties worldwide. Escrow-protected transactions, your own rates, and the freedom to trade on your terms.',
      bullets: ['Escrow-protected deals', 'Set your own rates', 'Global verified counterparties'],
      href: '/p2p-trading',
      accent: 'teal',
      imageRight: true,
    },
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">

      {/* ── HERO SECTION ── */}
      <section className="relative bg-slate-950 overflow-hidden" style={{ minHeight: 'calc(100vh - 0px)' }}>
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)',
            backgroundSize: '36px 36px'
          }} />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/25 to-transparent" />
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-amber-500/6 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -right-32 w-[480px] h-[480px] bg-slate-800/40 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16 lg:items-center min-h-screen pt-24 pb-12 lg:pt-0 lg:pb-0">

            {/* ── Left ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex flex-col justify-center space-y-7 lg:py-28"
            >
              {/* Eyebrow */}
              <div className="flex items-center gap-3">
                <div className="h-px w-8 bg-amber-500 flex-shrink-0" />
                <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.18em]">
                  Global Capital Markets
                </span>
              </div>

              {/* Headline — 2 lines, fully visible */}
              <div>
                <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-tight tracking-tight text-white">
                  Invest with{' '}
                  <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent">
                    Confidence.
                  </span>
                </h1>
                <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg">
                  INTRA CAPITAL LIMITED delivers professional investment and trading services 
                  across global capital markets — with institutional-grade precision, 
                  security, and world-class technology.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-row gap-3">
                <a
                  href="http://dashboard.intracapitals.com/"
                  className="group inline-flex items-center px-5 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm rounded-xl transition-colors duration-200 shadow-lg shadow-amber-500/20 whitespace-nowrap"
                >
                  Open Account
                  <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </a>
                <Link
                  to="/trading"
                  className="inline-flex items-center px-5 py-3 border border-white/20 text-white hover:bg-white/5 font-semibold text-sm rounded-xl transition-colors duration-200 whitespace-nowrap"
                >
                  Explore Platform
                </Link>
              </div>
            </motion.div>

            {/* ── Right: Video ── */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative flex flex-col justify-center mt-8 pb-8 lg:mt-0 lg:py-20"
            >
              {/* Outer glow */}
              <div className="absolute -inset-6 bg-gradient-to-br from-amber-500/8 via-transparent to-transparent rounded-3xl blur-2xl pointer-events-none" />

              {/* Video card */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60" style={{ aspectRatio: '16/10' }}>
                <video
                  src="/videos/15406867_1280_720_25fps.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                {/* Scrim */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

                {/* Overlay content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                  <div>
                    <p className="text-3xl font-black text-white leading-none">$50B+</p>
                    <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Assets Under Management</p>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 rounded-lg backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" />
                    <span className="text-emerald-400 text-xs font-semibold">Live Markets</span>
                  </div>
                </div>
              </div>

            </motion.div>

          </div>
        </div>
      </section>


      {/* ── ABOUT SECTION ── */}
      <section className="relative bg-slate-950 overflow-hidden">
        {/* Background accents */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />
          <div className="absolute top-1/3 -left-32 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-slate-700/40 rounded-full blur-3xl" />
        </div>

        {/* Full-width video */}
        <div className="relative w-full" style={{ aspectRatio: '16/7' }}>
          <video
            src="/videos/5207408-sd_426_240_25fps.mp4"
            autoPlay loop muted playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/30 to-slate-950" />

          {/* Floating live badge */}
          <div className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-full">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" />
            <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">Active Markets</span>
          </div>
        </div>

        {/* Content below video */}
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">

          {/* Section label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className="h-px w-8 bg-amber-500 flex-shrink-0" />
            <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.18em]">Who We Are</span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

            {/* Left: Headline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight tracking-tight">
                Your Trusted Partner in
                <span className="block bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent mt-2">
                  Global Capital Markets
                </span>
              </h2>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4 mt-10">
                {[
                  { value: '500K+', label: 'Active Clients' },
                  { value: '$50B+', label: 'Volume Traded' },
                  { value: '150+', label: 'Countries' },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="text-center p-4 lg:p-5 bg-white/5 border border-white/10 rounded-2xl hover:border-amber-500/40 hover:bg-amber-500/5 transition-all duration-300"
                  >
                    <div className="text-2xl lg:text-3xl font-black text-amber-400 mb-1">{stat.value}</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: Body copy + pillars */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="space-y-8"
            >
              <p className="text-lg text-slate-400 leading-relaxed">
                INTRA CAPITAL LIMITED is a leading investment and financial services firm 
                committed to delivering institutional-grade trading experiences across 
                global capital markets. With deep expertise in equities, digital assets, 
                forex, and alternative investments, we empower clients at every level.
              </p>
              <p className="text-lg text-slate-400 leading-relaxed">
                Our platform combines the precision of institutional tools with the 
                accessibility of modern design — making professional capital management 
                available to everyone. We prioritise security, transparency, and 
                innovation in everything we build.
              </p>

              {/* Pillars */}
              <div className="space-y-4 pt-2">
                {[
                  { title: 'Institutional Precision', desc: 'Professional-grade tools and execution built for serious capital allocators.' },
                  { title: 'Transparent by Design', desc: 'Clear fee structures, real-time reporting, and full regulatory compliance.' },
                  { title: 'Global Reach', desc: 'Access to equities, crypto, forex and alternatives across 150+ countries.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-amber-500" />
                    <div>
                      <h4 className="text-white font-semibold text-sm">{item.title}</h4>
                      <p className="text-slate-500 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold transition-colors duration-200"
              >
                Learn more about us
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </motion.div>

          </div>
        </div>
      </section>


      {/* ── SERVICES SECTION ── */}
      {/* Section header */}
      <div className="relative bg-slate-950 pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)',
            backgroundSize: '36px 36px'
          }} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative text-center mx-auto max-w-3xl px-4"
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-amber-500/50" />
            <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.18em]">What We Offer</span>
            <div className="h-px w-10 bg-amber-500/50" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">Our Investment Services</h2>
          <p className="text-lg text-slate-400">
            Comprehensive capital markets solutions designed for investors and traders of all levels
          </p>
        </motion.div>
      </div>

      {/* Individual service panels */}
      {services.map((service, index) => {
        const accentMap = {
          amber:  { tag: 'bg-amber-500/10 text-amber-400 border-amber-500/20',  bullet: 'bg-amber-500',  cta: 'text-amber-400 hover:text-amber-300',  glow: 'bg-amber-500/8',  border: 'border-amber-500/30' },
          violet: { tag: 'bg-violet-500/10 text-violet-400 border-violet-500/20', bullet: 'bg-violet-400', cta: 'text-violet-400 hover:text-violet-300', glow: 'bg-violet-500/8', border: 'border-violet-500/30' },
          emerald:{ tag: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', bullet: 'bg-emerald-400', cta: 'text-emerald-400 hover:text-emerald-300', glow: 'bg-emerald-500/8', border: 'border-emerald-500/30' },
          sky:    { tag: 'bg-sky-500/10 text-sky-400 border-sky-500/20',         bullet: 'bg-sky-400',    cta: 'text-sky-400 hover:text-sky-300',      glow: 'bg-sky-500/8',    border: 'border-sky-500/30' },
          teal:   { tag: 'bg-teal-500/10 text-teal-400 border-teal-500/20',      bullet: 'bg-teal-400',   cta: 'text-teal-400 hover:text-teal-300',    glow: 'bg-teal-500/8',   border: 'border-teal-500/30' },
        };
        const a = accentMap[service.accent];
        const isEven = index % 2 === 0;

        return (
          <section
            key={service.tag}
            className={`relative overflow-hidden ${isEven ? 'bg-slate-950' : 'bg-slate-900'}`}
          >
            {/* Subtle separator */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${service.imageRight ? '' : 'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1'}`}>

                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: service.imageRight ? 40 : -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.75 }}
                  className="relative"
                >
                  {/* Glow blob */}
                  <div className={`absolute -inset-4 ${a.glow} rounded-3xl blur-2xl pointer-events-none`} />
                  <div className={`relative rounded-2xl overflow-hidden border ${a.border} shadow-2xl aspect-[4/3] group`}>
                    <img
                      src={service.image}
                      alt={service.tag}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                    {/* Index number watermark */}
                    <div className="absolute top-4 left-4 text-7xl font-black text-white/5 leading-none select-none">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: service.imageRight ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.75, delay: 0.1 }}
                  className="space-y-6"
                >
                  {/* Tag */}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border ${a.tag}`}>
                    {service.tag}
                  </span>

                  {/* Headline */}
                  <h3 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white leading-tight whitespace-pre-line">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-400 text-base lg:text-lg leading-relaxed">
                    {service.description}
                  </p>

                  {/* Bullet features */}
                  <ul className="space-y-3">
                    {service.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-center gap-3 text-sm text-slate-300">
                        <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${a.bullet}`} />
                        {b}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={service.href}
                    className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200 ${a.cta}`}
                  >
                    Explore {service.tag}
                    <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </motion.div>

              </div>
            </div>
          </section>
        );
      })}


      {/* ── COPY TRADING SECTION ── */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-5">
              Let the Best Traders
              <span className="block bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent mt-1">
                Work for You
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Browse verified top performers, copy their trades automatically, and grow your portfolio without spending hours in front of charts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {[
              { name: 'Kwame Asante', username: '@kwame_fx', profit: '+412.3%', winRate: '89%', monthlyReturn: '+27.6%', risk: 'Medium', copiers: '3.2K', avatar: 'KA' },
              { name: 'Lena Hoffmann', username: '@lenatrades', profit: '+231.8%', winRate: '94%', monthlyReturn: '+19.4%', risk: 'Low', copiers: '2.5K', avatar: 'LH' },
              { name: 'Tariq Al-Rashid', username: '@tariq_capital', profit: '+589.1%', winRate: '81%', monthlyReturn: '+38.5%', risk: 'High', copiers: '4.7K', avatar: 'TR' }
            ].map((trader, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200 hover:border-amber-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {trader.avatar}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{trader.name}</h3>
                      <p className="text-sm text-slate-500">{trader.username}</p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-5">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="text-sm text-slate-500">Total Return</span>
                      <span className="text-lg font-bold text-emerald-600">{trader.profit}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-xl text-center">
                        <div className="font-bold text-slate-900">{trader.winRate}</div>
                        <div className="text-xs text-slate-500">Win Rate</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl text-center">
                        <div className="font-bold text-slate-900">{trader.monthlyReturn}</div>
                        <div className="text-xs text-slate-500">Monthly</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        trader.risk === 'Low' ? 'bg-emerald-50 text-emerald-700' :
                        trader.risk === 'Medium' ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700'
                      }`}>
                        {trader.risk} Risk
                      </span>
                      <span className="text-xs text-slate-400">{trader.copiers} copiers</span>
                    </div>
                    <a href="http://dashboard.intracapitals.com/" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200">
                      Copy
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/copy-trading"
              className="inline-flex items-center px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg"
            >
              Start Copy Trading
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>


      {/* ── LIVE MARKET DATA ── */}
      <section className="relative py-24 lg:py-32 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5">Live Market Intelligence</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Real-time market data with institutional accuracy. Track crypto, stocks, and indices.
            </p>
          </motion.div>

          <div className="flex justify-center mb-8">
            <div className="bg-slate-800/60 rounded-xl p-1.5 backdrop-blur-sm border border-slate-700/50">
              {['crypto', 'stocks', 'indices'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveMarketTab(tab)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 capitalize text-sm ${
                    activeMarketTab === tab
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {tab === 'indices' ? 'Indices' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <motion.div
            key={activeMarketTab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-xl font-bold text-white">
                  {activeMarketTab === 'crypto' ? 'Top Cryptocurrencies' : 
                   activeMarketTab === 'stocks' ? 'Major US Stocks' : 'Market Indices'}
                </h3>
                <p className="text-slate-500 text-sm mt-0.5">Refreshed in real-time</p>
              </div>
              <div className="flex items-center space-x-2 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-emerald-400 font-semibold text-xs uppercase tracking-wide">Live</span>
              </div>
            </div>

            <div className="space-y-2">
              {(activeMarketTab === 'crypto' ? liveMarketData.crypto : 
                activeMarketTab === 'stocks' ? liveMarketData.stocks : 
                liveMarketData.indices).slice(0, 6).map((item, index) => (
                <motion.div
                  key={item.symbol || index}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-colors duration-200 border border-slate-700/30"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center border border-amber-500/20 flex-shrink-0">
                      <span className="text-amber-400 font-bold text-sm">
                        {item.symbol?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">{item.symbol}</h4>
                      <p className="text-slate-500 text-xs">{item.name || ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-white">
                      ${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                    </div>
                    <div className={`text-xs font-semibold mt-0.5 ${item.positive ? 'text-emerald-400' : 'text-red-400'}`}>
                      {item.positive ? '▲' : '▼'} {Math.abs(parseFloat(item.change) || 0).toFixed(2)}%
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>


      {/* ── ANALYTICS SECTION ── */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
                <span className="text-amber-700 text-sm font-medium">Research & Analytics</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                Professional Market
                <span className="block bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent mt-1">
                  Research Tools
                </span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Access institutional-grade research and analytics powered by real-time data 
                and advanced charting tools used by professional investors worldwide.
              </p>
              <div className="space-y-5">
                {[
                  { icon: ChartBarIcon, title: 'Real-Time Data', desc: 'Live market data with 100+ technical indicators and pattern recognition.' },
                  { icon: BoltIcon, title: 'AI-Powered Insights', desc: 'Machine learning algorithms provide actionable signals and market forecasts.' },
                  { icon: ShieldCheckIcon, title: 'Risk Management', desc: 'Comprehensive portfolio analysis with intelligent risk assessment and alerts.' }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center border border-amber-100">
                      <feature.icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{feature.title}</h4>
                      <p className="text-slate-500 text-sm">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/news"
                className="inline-flex items-center px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg"
              >
                Explore Research
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl">
                <img
                  src="/images/new-images/market-analysis.jpg"
                  alt="Professional market analysis"
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.src = '/images/new-images/data-analytics.jpg'; }}
                />
                <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-slate-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900">94.7%</div>
                    <div className="text-xs text-slate-500">Signal Accuracy</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ── EXPERT TEAM SECTION ── */}
      <section className="relative py-24 lg:py-32 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 shadow-2xl">
                <img
                  src="/images/new-images/team-collaboration.jpg"
                  alt="Intra Capital professional team"
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.src = '/images/new-images/office-professional.jpg'; }}
                />
                <div className="absolute top-5 left-5 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/20">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-white font-medium text-sm">Live Trading Desk</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                <span className="text-amber-300 text-sm font-medium">Our Expert Team</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                World-Class Capital
                <span className="block bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent mt-1">
                  Markets Professionals
                </span>
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed">
                Our team of seasoned professionals brings decades of experience from leading 
                global financial institutions, delivering institutional-grade support and 
                market intelligence to every client.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '25+', label: 'Years of Experience' },
                  { value: '$2.5B+', label: 'Assets Managed' },
                  { value: '24/7', label: 'Market Monitoring' },
                  { value: '50+', label: 'Countries Served' }
                ].map((stat, index) => (
                  <div key={index} className="text-center p-5 bg-slate-900 border border-slate-800 rounded-xl hover:border-amber-500/30 transition-all duration-300">
                    <div className="text-2xl font-bold text-amber-400 mb-1">{stat.value}</div>
                    <div className="text-xs text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                className="inline-flex items-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-xl transition-all duration-300 shadow-lg"
              >
                Meet Our Team
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ── TECHNOLOGY SECTION ── */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center px-4 py-2 bg-slate-100 border border-slate-200 rounded-full">
                <span className="text-slate-600 text-sm font-medium">Next-Gen Technology</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                Cutting-Edge Trading
                <span className="block bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent mt-1">
                  Infrastructure
                </span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Experience lightning-fast execution and enterprise-grade security with our 
                state-of-the-art infrastructure built for the modern institutional investor.
              </p>
              <div className="space-y-5">
                {[
                  { icon: BoltIcon, title: 'Sub-Millisecond Speed', desc: 'Ultra-fast execution with advanced order routing and direct market access.' },
                  { icon: LockClosedIcon, title: 'Military-Grade Security', desc: 'End-to-end encryption with multi-layer security protocols and cold storage.' },
                  { icon: GlobeAltIcon, title: 'Global Infrastructure', desc: 'Worldwide server network with 99.99% uptime SLA guarantee.' }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200">
                      <feature.icon className="w-5 h-5 text-slate-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{feature.title}</h4>
                      <p className="text-slate-500 text-sm">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/trading"
                className="inline-flex items-center px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg"
              >
                Explore Platform
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl">
                <img
                  src="/images/new-images/trading-workspace.jpg"
                  alt="Advanced trading technology"
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.src = '/images/new-images/hero-trading-platform.jpg'; }}
                />
                <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-slate-200">
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900">&lt;1ms</div>
                    <div className="text-xs text-slate-500">Latency</div>
                  </div>
                </div>
                <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-slate-200">
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-600">99.99%</div>
                    <div className="text-xs text-slate-500">Uptime SLA</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ── TESTIMONIALS ── */}
      <section className="relative py-24 lg:py-32 bg-slate-50 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Trusted by Professionals</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">What industry professionals say about INTRA CAPITAL LIMITED</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-7 border border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start space-x-4 mb-5">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-slate-200"
                  />
                  <div>
                    <h4 className="font-bold text-slate-900">{testimonial.name}</h4>
                    <p className="text-sm text-slate-500">{testimonial.role}, {testimonial.company}</p>
                  </div>
                  <div className="ml-auto flex-shrink-0">
                    <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-semibold rounded-full border border-amber-100">
                      {testimonial.stats}
                    </span>
                  </div>
                </div>
                <p className="text-slate-600 leading-relaxed text-sm italic">"{testimonial.content}"</p>
                <div className="flex mt-4 gap-0.5">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ── CTA SECTION ── */}
      <section className="relative py-24 lg:py-32 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 right-0 w-80 h-80 bg-amber-500/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 left-0 w-80 h-80 bg-amber-400/6 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                <span className="text-amber-300 text-sm font-medium">Begin Your Journey</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                Ready to Unlock Your
                <span className="block bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent mt-1">
                  Investment Potential?
                </span>
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed">
                Join thousands of professionals who trust INTRA CAPITAL LIMITED for their 
                investment and trading needs. Open your account in minutes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="http://dashboard.intracapitals.com/"
                  className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-base rounded-xl transition-all duration-300 shadow-lg shadow-amber-500/20"
                >
                  Open Account Free
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </a>
                <Link
                  to="/trading"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/15 hover:border-amber-500/40 text-white font-semibold text-base rounded-xl transition-all duration-300"
                >
                  Explore Platform
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-800">
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400 mb-1">500K+</div>
                  <div className="text-xs text-slate-500">Active Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400 mb-1">$50B+</div>
                  <div className="text-xs text-slate-500">Volume Traded</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400 mb-1">150+</div>
                  <div className="text-xs text-slate-500">Countries</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 shadow-2xl group">
                <img
                  src="/images/new-images/capital-investment.jpg"
                  alt="Professional investment platform"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => { e.target.src = '/images/new-images/financial-growth.jpg'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>
                <div className="absolute top-5 right-5 bg-slate-900/90 backdrop-blur-sm rounded-xl p-4 border border-slate-700">
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">&lt;1ms</div>
                    <div className="text-xs text-slate-400">Execution</div>
                  </div>
                </div>
                <div className="absolute bottom-5 left-5 bg-slate-900/90 backdrop-blur-sm rounded-xl p-4 border border-slate-700">
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-400">99.99%</div>
                    <div className="text-xs text-slate-400">Uptime SLA</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
